import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Send, CheckCircle, AlertCircle, X, Home, Building2 } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Lead source options
const leadSources = [
  { value: 'google', label: 'Google Search' },
  { value: 'referral', label: 'Referral' },
  { value: 'social', label: 'Social Media' },
  { value: 'drive_by', label: 'Drove By / Signage' },
  { value: 'repeat', label: 'Repeat Customer' },
  { value: 'other', label: 'Other' },
];

const ContactForm = ({ serviceType = 'general', serviceName = '', onClose }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const location = useLocation();
  const isPsBrand = location.pathname.startsWith('/ps');
  const [enableResidentialPS, setEnableResidentialPS] = useState(true);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    property_type: 'residential',
    message: '',
    lead_source: '',
    urgency: 'normal'
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const colors = {
    primary: '#456CAC',
    text: isDark ? '#f1f5f9' : '#0f172a',
    textMuted: isDark ? '#94a3b8' : '#64748b',
    bg: isDark ? 'rgba(255,255,255,0.03)' : '#fff',
    inputBg: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
    border: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
  };

  // Fetch site settings for residential toggle
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/site-settings`);
        if (res.ok) {
          const data = await res.json();
          const enabled = data?.service_settings?.ps_residential_enabled ?? true;
          setEnableResidentialPS(enabled);
          // If PS brand and residential disabled, default to commercial
          if (isPsBrand && !enabled) {
            setFormData(prev => ({ ...prev, property_type: 'commercial' }));
          }
        }
      } catch (err) {
        // Default to enabled
      }
    };
    fetchSettings();
  }, [isPsBrand]);

  // Try to detect lead source from URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const source = params.get('utm_source') || params.get('ref');
    if (source) {
      setFormData(prev => ({ ...prev, lead_source: source }));
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      // Combine first/last name for backend compatibility
      const submitData = {
        ...formData,
        name: `${formData.first_name} ${formData.last_name}`.trim(),
        service_type: serviceType
      };
      
      await axios.post(`${API}/contact`, submitData);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    backgroundColor: colors.inputBg,
    border: `1px solid ${colors.border}`,
    color: colors.text
  };

  if (submitted) {
    return (
      <div 
        className="rounded-xl p-8 text-center"
        style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
      >
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-2xl font-bold mb-2" style={{ color: colors.text }}>
          Message Sent!
        </h3>
        <p style={{ color: colors.textMuted }}>
          Thank you for contacting us. We'll get back to you within 24 hours.
        </p>
        {onClose && (
          <Button 
            onClick={onClose}
            className="mt-6"
            style={{ backgroundColor: colors.primary, color: '#fff' }}
          >
            Close
          </Button>
        )}
      </div>
    );
  }

  return (
    <div 
      className="rounded-xl p-6"
      style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold" style={{ color: colors.text }}>
            Request a Quote
          </h3>
          {serviceName && (
            <p className="text-sm" style={{ color: colors.primary }}>{serviceName}</p>
          )}
        </div>
        {onClose && (
          <button 
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-[#456CAC]/10"
          >
            <X className="w-5 h-5" style={{ color: colors.textMuted }} />
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg flex items-center gap-2" style={{ backgroundColor: 'rgba(239,68,68,0.1)' }}>
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <span className="text-red-500 text-sm">{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Property Type Toggle - Hide Residential for PS if disabled */}
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: colors.textMuted }}>
            Property Type
          </label>
          <div className="flex gap-3">
            {/* Only show Residential if not PS brand OR if residential PS is enabled */}
            {(!isPsBrand || enableResidentialPS) && (
              <button
                type="button"
                onClick={() => setFormData({ ...formData, property_type: 'residential' })}
                className={`flex-1 p-3 rounded-lg flex items-center justify-center gap-2 transition-colors ${
                  formData.property_type === 'residential' ? 'ring-2 ring-[#456CAC]' : ''
                }`}
                style={{ 
                  ...inputStyle,
                  backgroundColor: formData.property_type === 'residential' ? `${colors.primary}15` : colors.inputBg
                }}
                data-testid="contact-residential-btn"
              >
                <Home className="w-5 h-5" style={{ color: formData.property_type === 'residential' ? colors.primary : colors.textMuted }} />
                <span style={{ color: formData.property_type === 'residential' ? colors.primary : colors.text }}>Residential</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => setFormData({ ...formData, property_type: 'commercial' })}
              className={`flex-1 p-3 rounded-lg flex items-center justify-center gap-2 transition-colors ${
                formData.property_type === 'commercial' ? 'ring-2 ring-[#456CAC]' : ''
              }`}
              style={{ 
                ...inputStyle,
                backgroundColor: formData.property_type === 'commercial' ? `${colors.primary}15` : colors.inputBg
              }}
              data-testid="contact-commercial-btn"
            >
              <Building2 className="w-5 h-5" style={{ color: formData.property_type === 'commercial' ? colors.primary : colors.textMuted }} />
              <span style={{ color: formData.property_type === 'commercial' ? colors.primary : colors.text }}>Commercial</span>
            </button>
          </div>
        </div>

        {/* Name Fields */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: colors.textMuted }}>
              First Name *
            </label>
            <input
              type="text"
              required
              data-testid="contact-first-name-input"
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              className="w-full p-3 rounded-lg"
              style={inputStyle}
              placeholder="John"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: colors.textMuted }}>
              Last Name
            </label>
            <input
              type="text"
              data-testid="contact-last-name-input"
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              className="w-full p-3 rounded-lg"
              style={inputStyle}
              placeholder="Smith"
            />
          </div>
        </div>

        {/* Contact Info */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: colors.textMuted }}>
              Email *
            </label>
            <input
              type="email"
              required
              data-testid="contact-email-input"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full p-3 rounded-lg"
              style={inputStyle}
              placeholder="john@example.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: colors.textMuted }}>
              Phone
            </label>
            <input
              type="tel"
              data-testid="contact-phone-input"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full p-3 rounded-lg"
              style={inputStyle}
              placeholder="(403) 555-1234"
            />
          </div>
        </div>

        {/* Company (show if commercial) */}
        {formData.property_type === 'commercial' && (
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: colors.textMuted }}>
              Company Name
            </label>
            <input
              type="text"
              data-testid="contact-company-input"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              className="w-full p-3 rounded-lg"
              style={inputStyle}
              placeholder="Your Company Ltd."
            />
          </div>
        )}

        {/* Message */}
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: colors.textMuted }}>
            Project Details *
          </label>
          <textarea
            required
            rows={4}
            data-testid="contact-message-input"
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            className="w-full p-3 rounded-lg resize-none"
            style={inputStyle}
            placeholder="Tell us about your fencing project - approximate length, fence style, any special requirements..."
          />
        </div>

        {/* Lead Source & Urgency */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: colors.textMuted }}>
              How did you hear about us?
            </label>
            <select
              data-testid="contact-source-input"
              value={formData.lead_source}
              onChange={(e) => setFormData({ ...formData, lead_source: e.target.value })}
              className="w-full p-3 rounded-lg"
              style={inputStyle}
            >
              <option value="">Select...</option>
              {leadSources.map(source => (
                <option key={source.value} value={source.value}>{source.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: colors.textMuted }}>
              Timeline
            </label>
            <select
              data-testid="contact-urgency-input"
              value={formData.urgency}
              onChange={(e) => setFormData({ ...formData, urgency: e.target.value })}
              className="w-full p-3 rounded-lg"
              style={inputStyle}
            >
              <option value="urgent">ASAP / Urgent</option>
              <option value="normal">Within 2-4 weeks</option>
              <option value="planning">Just planning / 1-3 months</option>
              <option value="future">Future project / 3+ months</option>
            </select>
          </div>
        </div>

        {/* Submit */}
        <Button
          type="submit"
          disabled={submitting}
          className="w-full py-6 text-lg gap-2"
          style={{ backgroundColor: colors.primary, color: '#fff' }}
        >
          {submitting ? (
            'Sending...'
          ) : (
            <>
              <Send className="w-5 h-5" />
              Send Request
            </>
          )}
        </Button>

        <p className="text-xs text-center" style={{ color: colors.textMuted }}>
          By submitting, you agree to our{' '}
          <a href="/privacy" className="underline" style={{ color: colors.primary }}>Privacy Policy</a>{' '}
          and consent to being contacted.
        </p>
        <p className="text-xs text-center" style={{ color: colors.textMuted }}>
          We typically respond within 24 hours. For urgent requests, call{' '}
          <a href="tel:4039865484" className="underline" style={{ color: colors.primary }}>(403) 986-5484</a>
        </p>
      </form>
    </div>
  );
};

export default ContactForm;
